import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Player } from '@remotion/player';
import { Download, Loader2, RotateCcw, ZoomIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { QuoteSlide } from '@/remotion/carousel/QuoteSlide';
import type { QuoteSlideProps } from '@/lib/quoteCarousel/buildQuoteSlideProps';
import { withBackgroundZoom } from '@/lib/quoteCarousel/slidePlacement';
import { SlideDragOverlay, type SlidePlacementPatch } from './SlideDragOverlay';

interface SlidePreviewProps {
  props: QuoteSlideProps;
  /** Style 'frame' slide whose background frame is not captured yet. */
  capturing: boolean;
  /** Drag/zoom result for the selected slide; absent = read-only preview. */
  onPlacementChange?: (patch: SlidePlacementPatch) => void;
  /** "Restaurar posição": clear the slide's text position and pan/zoom. */
  onResetPlacement?: () => void;
  /** "Baixar imagem" for the selected slide. */
  onDownloadImage?: () => void;
  downloadingImage?: boolean;
}

/** Full-size live preview of the selected slide — the same `QuoteSlide`
 *  composition the export renders, shown as a one-frame Player — with the
 *  drag overlay (move text / pan background) and its toolbar. Mount it keyed
 *  by slide so a draft never leaks onto another slide. */
export const SlidePreview: React.FC<SlidePreviewProps> = ({
  props, capturing, onPlacementChange, onResetPlacement, onDownloadImage, downloadingImage = false,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<SlidePlacementPatch | null>(null);
  // Preview == export: the draft only overrides the same props the export uses.
  const shownProps = useMemo<QuoteSlideProps>(() => (draft ? { ...props, ...draft } : props), [draft, props]);
  const zoom = shownProps.backgroundFrame?.scale ?? 1;
  const placed = !!props.textPosition || !!props.backgroundFrame;
  const editable = !!onPlacementChange;

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className="relative mx-auto max-w-[420px] overflow-hidden rounded-lg border bg-muted shadow-sm"
        // Width follows the 70vh height cap so tall 9:16 slides are never letterboxed.
        style={{
          aspectRatio: `${props.width} / ${props.height}`,
          width: `min(100%, calc(70vh * ${props.width} / ${props.height}))`,
        }}
        data-testid="quote-slide-preview"
      >
        <Player
          component={QuoteSlide as unknown as React.FC<Record<string, unknown>>}
          inputProps={shownProps}
          durationInFrames={1}
          fps={30}
          compositionWidth={props.width}
          compositionHeight={props.height}
          style={{ width: '100%', height: '100%' }}
          controls={false}
        />
        {editable && (
          <SlideDragOverlay
            slideProps={shownProps}
            onDraft={setDraft}
            onCommit={(patch) => onPlacementChange(patch)}
          />
        )}
        {capturing && (
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center bg-background/60"
            data-testid="quote-slide-preview-capturing"
          >
            <span className="flex items-center gap-2 rounded-md bg-background/90 px-3 py-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t('quoteCarousel.preview.capturing')}
            </span>
          </div>
        )}
      </div>

      {(editable || onDownloadImage) && (
        <div className="flex w-full max-w-[420px] flex-wrap items-center gap-2" data-testid="quote-slide-toolbar">
          {editable && props.backgroundUrl && (
            <div className="flex min-w-[140px] flex-1 items-center gap-2">
              <ZoomIn className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <Slider
                data-testid="quote-slide-zoom"
                min={1}
                max={3}
                step={0.05}
                value={[zoom]}
                onValueChange={([value]) => setDraft({ backgroundFrame: withBackgroundZoom(props.backgroundFrame ?? undefined, value) })}
                onValueCommit={([value]) => {
                  setDraft(null);
                  onPlacementChange({ backgroundFrame: withBackgroundZoom(props.backgroundFrame ?? undefined, value) });
                }}
                thumbProps={{ 'aria-label': t('quoteCarousel.placement.zoom') }}
              />
              <span className="w-10 text-right font-mono text-[11px] tabular-nums text-muted-foreground">
                {`${zoom.toFixed(1)}×`}
              </span>
            </div>
          )}
          {editable && onResetPlacement && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              data-testid="quote-slide-reset-placement"
              disabled={!placed}
              onClick={() => {
                setDraft(null);
                onResetPlacement();
              }}
            >
              <RotateCcw className="mr-1 h-3.5 w-3.5" />
              {t('quoteCarousel.placement.reset')}
            </Button>
          )}
          {onDownloadImage && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-auto h-8 text-xs"
              data-testid="quote-slide-download-image"
              disabled={downloadingImage}
              onClick={onDownloadImage}
            >
              {downloadingImage ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="mr-1 h-3.5 w-3.5" />
              )}
              {t('quoteCarousel.singleImage.download')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default SlidePreview;
